import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Container,
  Typography,
  TextField,
  MenuItem,
  Button,
  IconButton,
  Chip,
  Checkbox,
  ListItemText,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
  ToggleButton,
} from "@mui/material";
import {
  Edit,
  Close,
  Add,
  Delete,
  RestoreFromTrash,
  InfoOutlined,
} from "@mui/icons-material";
import HeaderInternal from "../components/Header/HeaderInternal";
import { FaqButton } from "../components/FaqButton";
import { FooterInternal } from "../components/Footer";
import AddButton from "../components/AddButton";
import ClearFiltersButton from "../components/ClearFiltersButton";
import ResultCount from "../components/ResultCount";
import StandardPagination, { ITENS_POR_PAGINA_PADRAO } from "../components/Pagination/StandardPagination";
import SavingOverlay from "../components/SavingOverlay";
import ConfirmModal from "../components/modals/ConfirmModal";
import { Toast } from "../components/Toast";
import { useToast } from "../hooks/useToast";
import { useRouter } from "../contexts/RouterContext";
import { useAuth } from "../contexts/AuthContext";
import {
  roomsService,
  Room,
  RoomAssignment,
  RoomProfessional,
} from "../services/roomsService";
import { entityUserService, OptionItem } from "../services/entityUserService";
import { colors, pageHeader } from "../theme/designSystem";

// Salas da entidade + sala padrão de cada profissional.
// A sala do atendimento é a sala padrão do profissional na data; remanejamento pontual
// não é registrado enquanto a agenda estiver no Bubble.

const NAME_MAX = 60;
const NOTES_MAX = 255;

// dias da semana no padrão do backend: 0 = domingo … 6 = sábado
const WEEKDAYS: { value: number; short: string; long: string }[] = [
  { value: 0, short: "Dom", long: "Domingo" },
  { value: 1, short: "Seg", long: "Segunda" },
  { value: 2, short: "Ter", long: "Terça" },
  { value: 3, short: "Qua", long: "Quarta" },
  { value: 4, short: "Qui", long: "Quinta" },
  { value: 5, short: "Sex", long: "Sexta" },
  { value: 6, short: "Sáb", long: "Sábado" },
];

const ALL_WEEKDAYS = WEEKDAYS.map((day) => day.value);

const sortWeekdays = (days: number[]): number[] => [...days].sort((a, b) => a - b);

/** Rótulo do chip de indisponibilidade na lista de salas; vazio quando a sala está sempre disponível. */
const rentalLabel = (days: number[]): string => {
  if (!days || days.length === 0) return "";
  if (days.length >= WEEKDAYS.length) return "Indisponível (todos os dias)";
  return `Indisponível: ${sortWeekdays(days)
    .map((value) => WEEKDAYS.find((day) => day.value === value)?.short || value)
    .join(", ")}`;
};

const today = (): string => new Date().toISOString().substring(0, 10);

const normalize = (value: string): string =>
  value.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase();

const filterFieldSx = {
  "& .MuiOutlinedInput-root": {
    height: "40px",
    fontSize: "1rem",
    backgroundColor: "white",
    "& fieldset": { borderColor: "#ced4da", legend: { maxWidth: "100%" } },
    "&:hover fieldset": { borderColor: "#ced4da" },
    "&.Mui-focused fieldset": {
      borderColor: colors.primary,
      boxShadow: "0 0 0 3px rgba(3, 180, 198, 0.1)",
    },
  },
  "& .MuiOutlinedInput-input, & .MuiSelect-select": {
    padding: "0.375rem 0.5rem",
    color: colors.textPrimary,
  },
  "& .MuiInputLabel-root": {
    fontSize: "0.95rem",
    color: colors.textSecondary,
    backgroundColor: "white",
    paddingLeft: "4px",
    paddingRight: "4px",
    "&.Mui-focused": { color: colors.primary },
  },
};

// Dentro do painel branco unico as secoes sao so espacamento: fundo e borda proprios
// virariam caixa dentro de caixa.
const sectionStyle = {
  mb: 3,
};

// alvo de toque de 44px no celular; 32px no desktop (padrão das listas do sistema)
const rowActionSx = (background: string, hover: string) => ({
  backgroundColor: background,
  "&:hover": { backgroundColor: hover },
});

interface RoomForm {
  id: string;
  name: string;
  capacity: string;
  rentalWeekdays: number[];
  notes: string;
  specialtyIds: string[];
}

const emptyRoomForm: RoomForm = {
  id: "",
  name: "",
  capacity: "",
  rentalWeekdays: [],
  notes: "",
  specialtyIds: [],
};

const Rooms: React.FC = () => {
  const { navigateTo } = useRouter();
  const { user } = useAuth();
  const { toast, showToast, hideToast } = useToast();

  // escopo da entidade da sessão (nunca de parâmetro da URL)
  const entityId = user?.entityId || "";

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(ITENS_POR_PAGINA_PADRAO);
  const [rooms, setRooms] = useState<Room[]>([]);
  const [professionals, setProfessionals] = useState<RoomProfessional[]>([]);
  const [professionalTypes, setProfessionalTypes] = useState<OptionItem[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>("");

  // filtros reativos da tela
  const [search, setSearch] = useState<string>("");
  const [specialty, setSpecialty] = useState<string>("");
  // "todas" por padrão: ao inativar uma sala ela continua visível na lista (como "Inativa"),
  // permitindo reativar; senão a sala sumiria e pareceria que o botão não funcionou.
  const [status, setStatus] = useState<"ativas" | "inativas" | "todas">("todas");

  // modal de sala (inclusão/edição)
  const [roomModalOpen, setRoomModalOpen] = useState<boolean>(false);
  const [roomForm, setRoomForm] = useState<RoomForm>(emptyRoomForm);
  const [nameError, setNameError] = useState<string>("");
  const [saving, setSaving] = useState<boolean>(false);

  // modal de inclusão de profissional em uma sala
  const [addRoomTarget, setAddRoomTarget] = useState<Room | null>(null);
  const [addProfessionalId, setAddProfessionalId] = useState<string>("");
  const [addError, setAddError] = useState<string>("");

  // remoção de profissional da sala (encerra a alocação vigente hoje)
  const [removeTarget, setRemoveTarget] = useState<RoomAssignment | null>(null);

  const [toToggle, setToToggle] = useState<Room | null>(null);

  const load = useCallback(async () => {
    if (!entityId) {
      setError("Sessão sem entidade definida. Refaça o login para continuar.");
      return;
    }
    setLoading(true);
    setError("");
    try {
      const [roomList, professionalList] = await Promise.all([
        roomsService.getByEntity(entityId, true),
        roomsService.getProfessionals(entityId),
      ]);
      setRooms(roomList);
      setProfessionals(professionalList);
    } catch {
      setError("Não foi possível carregar as salas. Tente novamente.");
    } finally {
      setLoading(false);
    }
  }, [entityId]);

  const loadOptions = useCallback(async () => {
    if (!entityId) return;
    try {
      const options = await entityUserService.getFormOptions(entityId);
      setProfessionalTypes(options.professionalTypes || []);
    } catch {
      // sem tipos profissionais a tela segue utilizável (especialidades ficam vazias)
      setProfessionalTypes([]);
    }
  }, [entityId]);

  useEffect(() => {
    document.title = "Salas";
    load();
    loadOptions();
  }, [load, loadOptions]);

  const hasFilterChanges = Boolean(search || specialty) || status !== "todas";

  const clearFilters = () => {
    setSearch("");
    setSpecialty("");
    setStatus("todas");
  };

  const filteredRooms = useMemo(
    () =>
      rooms.filter((room) => {
        const matchesSearch =
          !search.trim() || normalize(room.name).includes(normalize(search));
        const matchesSpecialty =
          !specialty || room.specialties.some((s) => s.professionalTypeId === specialty);
        const matchesStatus =
          status === "todas" || (status === "ativas" ? room.active : !room.active);
        return matchesSearch && matchesSpecialty && matchesStatus;
      }),
    [rooms, search, specialty, status],
  );

  const totalPages = Math.max(1, Math.ceil(filteredRooms.length / itemsPerPage));
  const pagedRooms = useMemo(
    () => filteredRooms.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage),
    [filteredRooms, currentPage, itemsPerPage],
  );

  // Mexeu no filtro, volta para a primeira pagina: senao a lista aparece vazia sem motivo
  useEffect(() => {
    setCurrentPage(1);
  }, [search, specialty, status]);

  const professionalsWithoutRoom = useMemo(
    () => professionals.filter((p) => !p.currentRoomId),
    [professionals],
  );

  const openCreate = () => {
    setRoomForm(emptyRoomForm);
    setNameError("");
    setRoomModalOpen(true);
  };

  // marca/desmarca um dia de indisponibilidade da sala (alugada, reservada etc.)
  const toggleRentalDay = (weekday: number) =>
    setRoomForm((f) => ({
      ...f,
      rentalWeekdays: f.rentalWeekdays.includes(weekday)
        ? f.rentalWeekdays.filter((d) => d !== weekday)
        : sortWeekdays([...f.rentalWeekdays, weekday]),
    }));

  const openEdit = (room: Room) => {
    setRoomForm({
      id: room.id,
      name: room.name,
      capacity: room.capacity != null ? String(room.capacity) : "",
      rentalWeekdays: sortWeekdays(room.rentalWeekdays || []),
      notes: room.notes || "",
      specialtyIds: room.specialties.map((s) => s.professionalTypeId),
    });
    setNameError("");
    setRoomModalOpen(true);
  };

  const handleSaveRoom = async () => {
    if (saving) return;
    if (!roomForm.name.trim()) {
      setNameError("Nome obrigatório");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name: roomForm.name.trim(),
        capacity: roomForm.capacity ? Number(roomForm.capacity) : null,
        rentalWeekdays: sortWeekdays(roomForm.rentalWeekdays),
        notes: roomForm.notes.trim() || null,
        specialtyIds: roomForm.specialtyIds,
      };
      if (roomForm.id) {
        await roomsService.update(roomForm.id, payload);
        showToast("Sala atualizada com sucesso.", "success");
      } else {
        await roomsService.create({ entityId, ...payload });
        showToast("Sala incluída com sucesso.", "success");
      }
      setRoomModalOpen(false);
      await load();
    } catch (err: unknown) {
      const message =
        (err as { response?: { data?: { description?: string } } })?.response?.data
          ?.description || "Erro ao salvar a sala. Tente novamente.";
      showToast(message, "error");
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async () => {
    if (!toToggle || saving) return;
    setSaving(true);
    try {
      await roomsService.setActive(toToggle.id, !toToggle.active);
      showToast(toToggle.active ? "Sala inativada." : "Sala ativada.", "success");
      setToToggle(null);
      await load();
    } catch {
      showToast("Erro ao alterar o status da sala.", "error");
    } finally {
      setSaving(false);
    }
  };

  const openAddProfessional = (room: Room) => {
    setAddRoomTarget(room);
    setAddProfessionalId("");
    setAddError("");
  };

  // atribuir MOVE o profissional para a sala; o backend encerra a alocação anterior
  const assignProfessional = async (ueprId: string, roomId: string) => {
    if (saving) return;
    setSaving(true);
    try {
      await roomsService.createAssignment({
        entityId,
        ueprId,
        roomId,
        validFrom: today(),
      });
      showToast("Profissional alocado à sala.", "success");
      await load();
    } catch (err: unknown) {
      const message =
        (err as { response?: { data?: { description?: string } } })?.response?.data
          ?.description || "Erro ao alocar o profissional.";
      showToast(message, "error");
    } finally {
      setSaving(false);
    }
  };

  const handleAddProfessional = async () => {
    if (!addRoomTarget || saving) return;
    if (!addProfessionalId) {
      setAddError("Escolha o profissional");
      return;
    }
    await assignProfessional(addProfessionalId, addRoomTarget.id);
    setAddRoomTarget(null);
  };

  const handleRemoveAssignment = async () => {
    if (!removeTarget || saving) return;
    setSaving(true);
    try {
      await roomsService.closeAssignment(removeTarget.id, today());
      showToast("Profissional removido da sala.", "success");
      setRemoveTarget(null);
      await load();
    } catch (err: unknown) {
      const message =
        (err as { response?: { data?: { description?: string } } })?.response?.data
          ?.description || "Erro ao remover o profissional da sala.";
      showToast(message, "error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", minHeight: "100vh" }}>
      <style>{`
        @media print {
          .login-header, .login-footer-component, .no-print { display: none !important; }
          body { background: #fff !important; }
          main { margin-top: 0 !important; padding: 0 !important; background: #fff !important; }
          * { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          @page { size: A4 landscape; margin: 10mm; }
          .rooms-scroll { overflow: visible !important; }
          .rooms-scroll > * { min-width: 0 !important; width: 100% !important; }
          thead { display: table-header-group; }
        }
      `}</style>

      <HeaderInternal
        showCTAButton={false}
        className="login-header"
        isLoggedIn={true}
        userEmail={user?.email || ""}
        userProfile={user?.role || ""}
        clinicName={user?.clinicName || "Clínica"}
        notificationCount={0}
        onRevalidateLogin={() => {}}
        onNotificationClick={() => {}}
        onUserClick={() => {}}
        onLogoClick={() => navigateTo("dashboard")}
      />

      <Box
        component="main"
        className="c4u-main"
      >
        <Container maxWidth={false} disableGutters sx={pageHeader.container}>
          <Box
            className="c4u-page-header c4u-page-header--in-panel"
          >
            <Box>
              <Typography variant="h4" className="c4u-page-header__title">
                Salas
              </Typography>
              <Typography variant="body2" className="c4u-page-header__subtitle">
                Salas da clínica e a sala padrão de cada profissional.
              </Typography>
            </Box>
            <Box className="c4u-page-header__actions no-print">
              <FaqButton />
            </Box>
          </Box>

          {/* Filtros */}
          <div className="c4u-panel">
            <div className="c4u-filters__row no-print">
              <div className="c4u-filters__fields">
                <TextField
                  size="small"
                  label="Busca por nome"
                  placeholder="Buscar sala"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  fullWidth
                  sx={filterFieldSx}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ maxLength: NAME_MAX }}
                />
                <TextField
                  select
                  size="small"
                  label="Especialidade"
                  value={specialty}
                  onChange={(e) => setSpecialty(e.target.value)}
                  fullWidth
                  sx={filterFieldSx}
                  InputLabelProps={{ shrink: true }}
                >
                  <MenuItem value="">Todas</MenuItem>
                  {professionalTypes.map((type) => (
                    <MenuItem key={type.id} value={type.id}>
                      {type.name}
                    </MenuItem>
                  ))}
                </TextField>
                <TextField
                  select
                  size="small"
                  label="Status"
                  value={status}
                  onChange={(e) => setStatus(e.target.value as "ativas" | "inativas" | "todas")}
                  fullWidth
                  sx={filterFieldSx}
                  InputLabelProps={{ shrink: true }}
                >
                  <MenuItem value="ativas">Ativas</MenuItem>
                  <MenuItem value="inativas">Inativas</MenuItem>
                  <MenuItem value="todas">Todas</MenuItem>
                </TextField>
              </div>

              <div className="c4u-filters__actions">
                <ClearFiltersButton
                  onClick={clearFilters}
                  disabled={!hasFilterChanges || loading}
                />
                <AddButton onClick={openCreate} title="Incluir sala" />
              </div>
            </div>

          {loading && (
            <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
              <CircularProgress sx={{ color: colors.primary }} />
            </Box>
          )}

          {!loading && error && (
            <Box sx={{ ...sectionStyle, textAlign: "center", color: colors.error }}>{error}</Box>
          )}

          {!loading && !error && (
            <>
              {professionalsWithoutRoom.length > 0 && (
                <Box className="c4u-info-box">
                  <InfoOutlined className="c4u-info-box__icon" sx={{ fontSize: "1.1rem" }} />
                  <Typography sx={{ fontSize: 13, color: colors.textSecondary }}>
                    {professionalsWithoutRoom.length}{" "}
                    {professionalsWithoutRoom.length === 1
                      ? "profissional está sem sala padrão"
                      : "profissionais estão sem sala padrão"}
                    . Os atendimentos aconteceram em alguma sala, mas o sistema não sabe qual — as
                    horas entram como "sem sala definida" na banda de ocupação do relatório.
                  </Typography>
                </Box>
              )}

              {/* Salas */}
              <Box sx={sectionStyle}>
                <ResultCount shown={filteredRooms.length} total={rooms.length} />
                <>
                  {filteredRooms.length === 0 ? (
                    <Typography sx={{ fontSize: 13, color: colors.textSecondary, py: 2 }}>
                      {rooms.length === 0
                        ? "Nenhuma sala cadastrada — inclua a primeira sala no botão acima."
                        : "Nenhuma sala encontrada com os filtros aplicados."}
                    </Typography>
                  ) : (
                    <Box className="admin-plans-list-container" sx={{ background: "none", border: "none", borderRadius: 0, boxShadow: "none" }}>
                      <Box className="admin-plans-table admin-entities-table">
                        <Box className="c4u-table__header">
                          <Box
                            className="admin-plans-header-cell"
                            sx={{ flex: "2 1 280px", minWidth: 240 }}
                          >
                            Sala
                          </Box>
                          <Box className="admin-plans-header-cell c4u-col--xl" sx={{ flex: "0 0 110px" }}>
                            Capacidade
                          </Box>
                          <Box className="admin-plans-header-cell c4u-col--md" sx={{ flex: "0 0 150px" }}>
                            Indisponibilidade
                          </Box>
                          <Box
                            className="admin-plans-header-cell c4u-col--lg"
                            sx={{ flex: "1 1 220px", minWidth: 160 }}
                          >
                            Especialidades
                          </Box>
                          <Box
                            className="admin-plans-header-cell c4u-col--sm"
                            sx={{ flex: "1 1 280px", minWidth: 200 }}
                          >
                            Profissionais
                          </Box>
                          <Box className="admin-plans-header-cell" sx={{ flex: "0 0 110px" }}>
                            Status
                          </Box>
                          <Box
                            className="admin-plans-header-cell no-print"
                            sx={{ flex: "0 0 120px", justifyContent: "flex-end" }}
                          >
                            Ações
                          </Box>
                        </Box>

                        <Box className="admin-plans-table-body">
                          {pagedRooms.map((room) => {
                            const currentAssignments = room.assignments.filter((a) => a.current);
                            return (
                              <Box
                                key={room.id}
                                className="c4u-table__row"
                              >
                                <Box
                                  className="admin-plans-cell admin-plans-name"
                                  sx={{ flex: "2 1 280px", minWidth: 240, gap: 0.5, flexWrap: "wrap" }}
                                  data-label="Sala"
                                >
                                  <Typography sx={{ fontSize: 14, fontWeight: 600 }}>
                                    {room.name}
                                  </Typography>
                                  {room.notes && (
                                    <Typography
                                      sx={{ fontSize: 11, color: "#adb5bd", width: "100%" }}
                                      title={room.notes}
                                    >
                                      {room.notes}
                                    </Typography>
                                  )}
                                </Box>
                                <Box
                                  className="admin-plans-cell c4u-col--xl"
                                  sx={{ flex: "0 0 110px" }}
                                  data-label="Capacidade"
                                >
                                  {room.capacity != null ? `${room.capacity} lugares` : "-"}
                                </Box>
                                <Box
                                  className="admin-plans-cell c4u-col--md"
                                  sx={{ flex: "0 0 150px", whiteSpace: "normal" }}
                                  data-label="Indisponibilidade"
                                >
                                  {rentalLabel(room.rentalWeekdays) ? (
                                    <Chip
                                      label={rentalLabel(room.rentalWeekdays).replace("Indisponível: ", "")}
                                      size="small"
                                      title={`Indisponível em: ${sortWeekdays(room.rentalWeekdays)
                                        .map(
                                          (value) =>
                                            WEEKDAYS.find((day) => day.value === value)?.long || value,
                                        )
                                        .join(", ")}`}
                                      sx={{
                                        backgroundColor: "#6610f2",
                                        color: "white",
                                        fontWeight: 600,
                                        height: 20,
                                        fontSize: 11,
                                      }}
                                    />
                                  ) : (
                                    "-"
                                  )}
                                </Box>
                                <Box
                                  className="admin-plans-cell c4u-col--lg"
                                  sx={{
                                    flex: "1 1 220px",
                                    minWidth: 160,
                                    whiteSpace: "normal",
                                    wordBreak: "break-word",
                                  }}
                                  data-label="Especialidades"
                                  title={room.specialties.map((s) => s.professionalTypeName).join(", ")}
                                >
                                  {room.specialties.length === 0
                                    ? "-"
                                    : room.specialties.map((s) => s.professionalTypeName).join(", ")}
                                </Box>
                                <Box
                                  className="admin-plans-cell c4u-col--sm"
                                  sx={{ flex: "1 1 280px", minWidth: 200 }}
                                  data-label="Profissionais"
                                >
                                  <Box
                                    sx={{
                                      display: "flex",
                                      flexWrap: "wrap",
                                      alignItems: "center",
                                      gap: 0.5,
                                      width: "100%",
                                    }}
                                  >
                                    {currentAssignments.map((assignment) => (
                                      <Chip
                                        key={assignment.id}
                                        label={assignment.professionalName}
                                        size="small"
                                        onDelete={
                                          room.active
                                            ? (e: React.MouseEvent) => {
                                                e.stopPropagation();
                                                setRemoveTarget(assignment);
                                              }
                                            : undefined
                                        }
                                        sx={{
                                          maxWidth: "100%",
                                          backgroundColor: colors.primaryLight,
                                          color: colors.textPrimary,
                                          fontWeight: 500,
                                        }}
                                      />
                                    ))}
                                    {room.active && (
                                      <Button
                                        size="small"
                                        className="no-print"
                                        startIcon={<Add sx={{ fontSize: "1rem" }} />}
                                        onClick={(e) => { e.stopPropagation(); openAddProfessional(room); }}
                                        sx={{
                                          textTransform: "none",
                                          fontSize: 12,
                                          color: colors.primary,
                                          minHeight: { xs: 44, sm: 28 },
                                          px: 1,
                                        }}
                                      >
                                        Profissional
                                      </Button>
                                    )}
                                  </Box>
                                </Box>
                                <Box
                                  className="admin-plans-cell"
                                  sx={{ flex: "0 0 110px" }}
                                  data-label="Status"
                                >
                                  <Chip
                                    label={room.active ? "Ativa" : "Inativa"}
                                    size="small"
                                    sx={{
                                      color: "white",
                                      fontWeight: 600,
                                      backgroundColor: room.active ? colors.success : "#adb5bd",
                                    }}
                                  />
                                </Box>
                                <Box
                                  className="admin-plans-cell admin-plans-actions no-print"
                                  sx={{ flex: "0 0 120px" }}
                                  data-label="Ações"
                                >
                                  <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end", width: "100%" }}>
                                    <Tooltip title="Editar sala">
                                      <IconButton
                                        className="c4u-row-action"
                                        size="small"
                                        onClick={(e) => { e.stopPropagation(); openEdit(room); }}
                                        sx={rowActionSx(colors.primary, colors.primaryHover)}
                                      >
                                        <Edit sx={{ fontSize: "1rem" }} />
                                      </IconButton>
                                    </Tooltip>
                                    <Tooltip title={room.active ? "Inativar sala" : "Reativar sala"}>
                                      <IconButton
                                        className="c4u-row-action"
                                        size="small"
                                        onClick={(e) => { e.stopPropagation(); setToToggle(room); }}
                                        sx={rowActionSx(
                                          room.active ? "#dc3545" : colors.success,
                                          room.active ? "#c82333" : "#218838",
                                        )}
                                      >
                                        {room.active ? (
                                          <Delete sx={{ fontSize: "1rem" }} />
                                        ) : (
                                          <RestoreFromTrash sx={{ fontSize: "1rem" }} />
                                        )}
                                      </IconButton>
                                    </Tooltip>
                                  </Box>
                                </Box>
                              </Box>
                            );
                          })}
                        </Box>
                      </Box>
                    </Box>
                  )}

                  {filteredRooms.length > 0 && (
                    <StandardPagination
                      currentPage={currentPage}
                      totalPages={totalPages}
                      itemsPerPage={itemsPerPage}
                      totalItems={filteredRooms.length}
                      onPageChange={setCurrentPage}
                      onItemsPerPageChange={(n) => {
                        setItemsPerPage(n);
                        setCurrentPage(1);
                      }}
                    />
                  )}
                </>
              </Box>
            </>
          )}
          </div>
        </Container>
      </Box>

      <FooterInternal className="login-footer-component" />

      {/* Modal de sala */}
      <Dialog
        open={roomModalOpen}
        onClose={() => !saving && setRoomModalOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: "12px", position: "relative", overflow: "hidden" } }}
      >
        <SavingOverlay open={saving} />
        <DialogTitle
          sx={{
            backgroundColor: colors.primary,
            color: "white",
            fontWeight: 600,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          {roomForm.id ? "Editar sala" : "Nova sala"}
          <IconButton
            onClick={() => setRoomModalOpen(false)}
            disabled={saving}
            sx={{
              color: "white",
              "&:hover": { backgroundColor: "rgba(255,255,255,0.1)" },
              "&.Mui-disabled": { color: "rgba(255,255,255,0.4)" },
            }}
          >
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: "1.5rem", pt: "1.5rem !important" }}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <Box sx={{ display: "flex", flexDirection: { xs: "column", sm: "row" }, gap: 2 }}>
              <TextField
                label="Nome da sala"
                value={roomForm.name}
                onChange={(e) => {
                  setRoomForm((f) => ({ ...f, name: e.target.value }));
                  if (nameError) setNameError("");
                }}
                error={!!nameError}
                helperText={nameError}
                inputProps={{ maxLength: NAME_MAX }}
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                label="Capacidade"
                value={roomForm.capacity}
                onChange={(e) =>
                  setRoomForm((f) => ({
                    ...f,
                    capacity: e.target.value.replace(/\D/g, "").slice(0, 3),
                  }))
                }
                inputProps={{ inputMode: "numeric", maxLength: 3 }}
                placeholder="Lugares"
                sx={{ width: { xs: "100%", sm: 150 }, flexShrink: 0 }}
                InputLabelProps={{ shrink: true }}
              />
            </Box>

            <TextField
              select
              label="Especialidades atendidas"
              value={roomForm.specialtyIds}
              onChange={(e) => {
                const selected = e.target.value as unknown as string[];
                setRoomForm((f) => ({ ...f, specialtyIds: selected }));
              }}
              fullWidth
              InputLabelProps={{ shrink: true }}
              SelectProps={{
                multiple: true,
                renderValue: (selected) => {
                  const ids = selected as string[];
                  if (ids.length === 0) return "Todas";
                  return professionalTypes
                    .filter((t) => ids.includes(t.id))
                    .map((t) => t.name)
                    .join(", ");
                },
              }}
              helperText="Em branco = sala sem restrição de especialidade"
            >
              {professionalTypes.map((type) => (
                <MenuItem key={type.id} value={type.id}>
                  <Checkbox
                    size="small"
                    checked={roomForm.specialtyIds.includes(type.id)}
                    sx={{ p: 0.5, mr: 1 }}
                  />
                  <ListItemText primary={type.name} />
                </MenuItem>
              ))}
            </TextField>

            <Box
              sx={{
                p: 1.5,
                borderRadius: "8px",
                border: "1px solid #e9ecef",
                backgroundColor: colors.background,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: { xs: "column", sm: "row" },
                  alignItems: { xs: "flex-start", sm: "center" },
                  justifyContent: "space-between",
                  gap: 1,
                  mb: 1,
                }}
              >
                <Box>
                  <Typography sx={{ fontSize: 14, fontWeight: 600, color: colors.textPrimary }}>
                    Dias indisponíveis
                  </Typography>
                  <Typography sx={{ fontSize: 12, color: colors.textSecondary }}>
                    Dias em que a sala fica indisponível para a clínica (alugada, reservada para
                    reunião etc.). Nesses dias ela não conta como sala disponível.
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", gap: 0.5, flexShrink: 0 }}>
                  <Button
                    size="small"
                    onClick={() => setRoomForm((f) => ({ ...f, rentalWeekdays: ALL_WEEKDAYS }))}
                    disabled={roomForm.rentalWeekdays.length === ALL_WEEKDAYS.length}
                    sx={{ textTransform: "none", fontSize: 12, color: colors.primary }}
                  >
                    Semana toda
                  </Button>
                  <Button
                    size="small"
                    onClick={() => setRoomForm((f) => ({ ...f, rentalWeekdays: [] }))}
                    disabled={roomForm.rentalWeekdays.length === 0}
                    sx={{ textTransform: "none", fontSize: 12, color: colors.textSecondary }}
                  >
                    Limpar
                  </Button>
                </Box>
              </Box>

              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
                {WEEKDAYS.map((day) => (
                  <ToggleButton
                    key={day.value}
                    value={day.value}
                    aria-label={day.long}
                    selected={roomForm.rentalWeekdays.includes(day.value)}
                    onChange={() => toggleRentalDay(day.value)}
                    sx={{
                      flex: { xs: "1 1 60px", sm: "0 0 auto" },
                      minWidth: { xs: 44, sm: 52 },
                      minHeight: 44,
                      border: "1px solid #ced4da",
                      borderRadius: "8px",
                      textTransform: "none",
                      fontSize: 13,
                      color: colors.textSecondary,
                      backgroundColor: "white",
                      "&.Mui-selected": {
                        backgroundColor: "#6610f2",
                        color: "white",
                        "&:hover": { backgroundColor: "#560bd0" },
                      },
                    }}
                  >
                    {day.short}
                  </ToggleButton>
                ))}
              </Box>

              <Typography sx={{ fontSize: 12, color: colors.textSecondary, mt: 1 }}>
                {roomForm.rentalWeekdays.length === 0
                  ? "Sala disponível para a clínica em todos os dias."
                  : roomForm.rentalWeekdays.length === ALL_WEEKDAYS.length
                    ? "Indisponível todos os dias: a sala fica fora do pool de capacidade."
                    : `Indisponível em ${roomForm.rentalWeekdays
                        .map((value) => WEEKDAYS.find((day) => day.value === value)?.long)
                        .join(", ")}.`}
              </Typography>
            </Box>

            <TextField
              label="Observação"
              value={roomForm.notes}
              onChange={(e) => setRoomForm((f) => ({ ...f, notes: e.target.value }))}
              inputProps={{ maxLength: NOTES_MAX }}
              multiline
              minRows={2}
              fullWidth
              InputLabelProps={{ shrink: true }}
              helperText={`${roomForm.notes.length}/${NOTES_MAX}`}
              // Neutraliza a regra global "textarea" (global.css) que força min-height/border/padding
              // e quebra o campo multiline do MUI — o textarea-sombra vazava por cima dos botões.
              sx={{
                "& textarea": {
                  minHeight: "0 !important",
                  border: "none !important",
                  padding: "0 !important",
                  resize: "none",
                  boxSizing: "border-box",
                },
              }}
            />
          </Box>
        </DialogContent>
        <DialogActions
          sx={{
            p: "1rem 1.5rem",
            gap: 1,
            justifyContent: "flex-end",
            "& .MuiButton-root": { flexShrink: 0, minHeight: 44 },
          }}
        >
          <Button
            onClick={() => setRoomModalOpen(false)}
            disabled={saving}
            variant="outlined"
            sx={{ textTransform: "none" }}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleSaveRoom}
            disabled={saving}
            variant="contained"
            sx={{
              backgroundColor: colors.primary,
              textTransform: "none",
              "&.Mui-disabled": { backgroundColor: colors.primary, opacity: 0.7, color: "white" },
            }}
          >
            {saving ? "Processando..." : "Salvar"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal de inclusão de profissional em uma sala */}
      <Dialog
        open={!!addRoomTarget}
        onClose={() => !saving && setAddRoomTarget(null)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: "12px", position: "relative", overflow: "hidden" } }}
      >
        <SavingOverlay open={saving} />
        <DialogTitle
          sx={{
            backgroundColor: colors.primary,
            color: "white",
            fontWeight: 600,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          Alocar profissional
          <IconButton
            onClick={() => setAddRoomTarget(null)}
            disabled={saving}
            sx={{
              color: "white",
              "&:hover": { backgroundColor: "rgba(255,255,255,0.1)" },
              "&.Mui-disabled": { color: "rgba(255,255,255,0.4)" },
            }}
          >
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: "1.5rem", pt: "1.5rem !important" }}>
          <Typography variant="body2" sx={{ mb: 2, color: colors.textSecondary }}>
            Aloca um profissional à sala <strong>{addRoomTarget?.name}</strong>. Se o profissional
            já estiver em outra sala, a alocação anterior é encerrada automaticamente.
          </Typography>
          <TextField
            select
            label="Profissional"
            value={addProfessionalId}
            onChange={(e) => {
              setAddProfessionalId(e.target.value);
              if (addError) setAddError("");
            }}
            error={!!addError && !addProfessionalId}
            helperText={addError}
            fullWidth
            InputLabelProps={{ shrink: true }}
          >
            {(() => {
              const options = professionals.filter(
                (p) => p.currentRoomId !== addRoomTarget?.id,
              );
              if (options.length === 0) {
                return (
                  <MenuItem value="" disabled>
                    Todos os profissionais já estão nesta sala
                  </MenuItem>
                );
              }
              return options.map((professional) => (
                <MenuItem key={professional.ueprId} value={professional.ueprId}>
                  {professional.currentRoomName
                    ? `${professional.professionalName} — atualmente na ${professional.currentRoomName}`
                    : professional.professionalName}
                </MenuItem>
              ));
            })()}
          </TextField>
        </DialogContent>
        <DialogActions
          sx={{
            p: "1rem 1.5rem",
            gap: 1,
            justifyContent: "flex-end",
            "& .MuiButton-root": { flexShrink: 0, minHeight: 44 },
          }}
        >
          <Button
            onClick={() => setAddRoomTarget(null)}
            disabled={saving}
            variant="outlined"
            sx={{ textTransform: "none" }}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleAddProfessional}
            disabled={saving}
            variant="contained"
            sx={{
              backgroundColor: colors.primary,
              textTransform: "none",
              "&.Mui-disabled": { backgroundColor: colors.primary, opacity: 0.7, color: "white" },
            }}
          >
            {saving ? "Processando..." : "Alocar"}
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmModal
        isOpen={!!removeTarget}
        title="Remover profissional da sala"
        message={
          removeTarget
            ? `Remover ${removeTarget.professionalName} da sala "${removeTarget.roomName}"?`
            : ""
        }
        warningMessage="A alocação vigente é encerrada. Sem sala padrão, as horas do profissional passam a contar como horas sem sala definida no relatório de ocupação."
        confirmButtonText="Remover"
        cancelButtonText="Cancelar"
        onConfirm={handleRemoveAssignment}
        onCancel={() => setRemoveTarget(null)}
        type="danger"
        isProcessing={saving}
      />

      <ConfirmModal
        isOpen={!!toToggle}
        title={toToggle?.active ? "Inativar sala" : "Ativar sala"}
        message={
          toToggle
            ? `Tem certeza que deseja ${toToggle.active ? "inativar" : "ativar"} a sala "${
                toToggle.name
              }"?`
            : ""
        }
        warningMessage={
          toToggle?.active
            ? "A sala deixa de aparecer para novas alocações e sai do cálculo de ocupação. É possível reativar depois."
            : undefined
        }
        confirmButtonText={toToggle?.active ? "Inativar" : "Ativar"}
        cancelButtonText="Cancelar"
        onConfirm={handleToggleActive}
        onCancel={() => setToToggle(null)}
        type={toToggle?.active ? "danger" : "info"}
        isProcessing={saving}
      />

      <Toast
        message={toast.message}
        type={toast.type}
        isVisible={toast.isVisible}
        onClose={hideToast}
      />
    </Box>
  );
};

export default Rooms;
